import React, { useCallback } from 'react';
import { KoobTooltip, KoobButton, KoobInputToggleField } from '@koob/ui';
import { Formik, FieldArray, Form } from 'formik';
import * as Yup from 'yup';
import { useTheme } from 'styled-components';
import { useAsync } from 'react-async';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@koob/dates';
import { parseBoolean } from 'utils';
import { getHotelsContracts } from 'api/node/contracts';
import { ToggleSection } from 'components';
import { TextField } from 'components/Fields';
import KoobTitle from 'components/Koob/KoobTitle';
import KoobPlaceholder from 'components/Koob/KoobPlaceholder';
import { getRoomsDetail } from '../../../../utils/hotelDistribution';
import EyeButton from './EyeButton';
import MarginFields from './MarginFields';
import { Stack } from '@koob/margaret';

const HotelDetailForm = ({
  hotel,
  onSelectionFinished,
  dmcOrganization,
  toOrganization,
  contracts,
  setHotelAttribute,
}) => {
  const theme = useTheme();
  const { t } = useTranslation('toConnectionRequest');

  const { data, isLoading } = useAsync({
    promiseFn: getHotelsContracts,
    hotelId: hotel.id,
  });

  const canDisplayPeriod = useCallback(
    (period) => {
      if (!contracts) {
        return true;
      }
      const contract = contracts?.find((contract) => {
        return (
          new Date(contract.startAt) <= new Date(period.startAt) &&
          new Date(contract.endAt) >= new Date(period.endAt)
        );
      });
      return !contract?.enabled || contract?.enabled === 'true';
    },
    [contracts],
  );

  const onSubmit = useCallback(
    (values) => {
      const rooms = values.rooms;
      const periods = [];
      rooms.forEach((room) => {
        room.periods.forEach((period) => {
          const newPeriod = {
            id: period.id,
            allotmentCount: period.toElement.allotmentCount,
            freeSale: period.toElement.freeSale,
            isPeriodAllowedForBooking: parseBoolean(room.enabled ?? false),
            isMarginPercent: period.toElement?.isMarginPercent,
            margin: period.toElement?.margin || null,
          };
          if (!room.enabled) {
            newPeriod.allotmentCount = 0;
            newPeriod.freeSale = false;
          }
          periods.push(newPeriod);
        });
      });
      setHotelAttribute(hotel.id, 'periods', periods);
      onSelectionFinished();
    },
    [hotel, onSelectionFinished],
  );

  return (
    <>
      {isLoading && (
        <div className="flex-col space-y-5">
          <KoobPlaceholder className="h-20 w-full rounded-md" />
          <KoobPlaceholder className="h-20 w-full rounded-md" />
          <KoobPlaceholder className="h-20 w-full rounded-md" />
        </div>
      )}

      {!isLoading && (
        <>
          <div className="text-center max-w-lg mx-auto">
            <KoobTitle size="text-xl">
              {t('hotelModalTitle', {
                hotelName: hotel.displayName,
                interpolation: { escapeValue: false },
              })}
            </KoobTitle>

            {data?.data.length === 0 && (
              <KoobTitle className="m-4" size="text-sm">
                {t('roomModalInformation')}
              </KoobTitle>
            )}
          </div>

          <Formik
            initialValues={{
              rooms: getRoomsDetail(
                data,
                hotel.periods,
                dmcOrganization,
                toOrganization,
              ),
            }}
            validationSchema={Yup.object().shape({
              rooms: Yup.array().of(
                Yup.object().shape({
                  periods: Yup.array().of(
                    Yup.object().shape({
                      id: Yup.string().required(
                        t('required', { ns: 'errors' }),
                      ),
                      toElement: Yup.object().shape({
                        allotmentCount: Yup.number()
                          .required(t('required', { ns: 'errors' }))
                          .test(
                            'is-allotment-valid',
                            () => t('allotmentNotValid'),
                            async (allotmentCount, testContext) => {
                              return (
                                allotmentCount >= 0 &&
                                allotmentCount <=
                                  testContext.parent
                                    .totalAllotmentsAvailableForDistribution
                              );
                            },
                          ),
                        freeSale: Yup.boolean().required(
                          t('required', { ns: 'errors' }),
                        ),
                        isPeriodAllowedForBooking: Yup.boolean().required(
                          t('required', { ns: 'errors' }),
                        ),
                      }),
                    }),
                  ),
                }),
              ),
            })}
            enableReinitialize
            onSubmit={onSubmit}
            validateOnBlur={true}
            validateOnChange={false}
          >
            {({ values, setFieldValue, isValid, isSubmitting }) => (
              <Form>
                <FieldArray
                  name="rooms"
                  render={(arrayHelpers) =>
                    values.rooms.map((room, roomIndex) => {
                      const isDisabled =
                        room.periods.filter(canDisplayPeriod).length === 0;

                      return (
                        <div
                          className="border m-5 p-2 rounded-md"
                          key={room.id}
                        >
                          <ToggleSection
                            title={room.name}
                            variant="full"
                            disabled={!parseBoolean(room.enabled)}
                            rightElement={
                              <KoobTooltip
                                disabled={!isDisabled}
                                tip={t(
                                  'bulkEdit.editor.periods.periodRequiredForVisibility',
                                )}
                              >
                                <EyeButton
                                  room={room}
                                  roomIndex={roomIndex}
                                  setFieldValue={setFieldValue}
                                  disabled={isDisabled}
                                  className={`${room.name
                                    ?.toLowerCase()
                                    .split(' ')
                                    .join('-')} ${
                                    parseBoolean(room.enabled)
                                      ? 'enabled'
                                      : 'disabled'
                                  }`}
                                />
                              </KoobTooltip>
                            }
                          >
                            <div>
                              {room.periods.map(
                                (period, periodIndex) =>
                                  canDisplayPeriod(period) && (
                                    <div
                                      key={period.id}
                                      className="grid grid-cols-5 gap-5 mb-10"
                                    >
                                      <h3
                                        className={`text-base font-bold text-k-h`}
                                        style={{ color: theme.gray }}
                                      >
                                        {t('periodDates', {
                                          start: formatDate(
                                            period.startAt,
                                            'DD-MM-YYYY',
                                          ),
                                          end: formatDate(
                                            period.endAt,
                                            'DD-MM-YYYY',
                                          ),
                                        })}
                                      </h3>

                                      <KoobTooltip
                                        tip={period.allAllocations
                                          .sort((a, b) => {
                                            if (
                                              a.organization.scope ===
                                              b.organization.scope
                                            ) {
                                              return (
                                                b.allotmentCount -
                                                a.allotmentCount
                                              );
                                            }
                                            return a.organization.scope.localeCompare(
                                              b.organization.scope,
                                            );
                                          })
                                          .map((allocation) => (
                                            <p key={allocation.id}>
                                              {t('allocation', {
                                                name: allocation.organization
                                                  .displayName,
                                                count:
                                                  allocation.allotmentCount,
                                                totalCount:
                                                  period.totalAllotmentsAvailable,
                                              })}
                                            </p>
                                          ))}
                                        position="bottom"
                                      >
                                        <TextField
                                          name={`rooms.${roomIndex}.periods.${periodIndex}.toElement.allotmentCount`}
                                          label={t('allotment')}
                                          disabled={
                                            values.rooms?.[roomIndex]
                                              ?.periods?.[periodIndex]
                                              ?.toElement?.freeSale
                                          }
                                          type="number"
                                          step="1"
                                          min="0"
                                        />
                                      </KoobTooltip>

                                      <div className="w-2/5">
                                        <KoobInputToggleField
                                          name={`rooms.${roomIndex}.periods.${periodIndex}.toElement.freeSale`}
                                          disabled={!period.freeSale}
                                          label={t('freeSale')}
                                          testId="free-sales"
                                        />
                                      </div>

                                      <div className="col-span-2">
                                        <MarginFields
                                          name={`rooms.${roomIndex}.periods.${periodIndex}.toElement`}
                                        />
                                      </div>
                                    </div>
                                  ),
                              )}
                            </div>
                          </ToggleSection>
                        </div>
                      );
                    })
                  }
                />

                <Stack
                  alignX="flex-end"
                  style={{
                    marginTop: '1rem',
                  }}
                  gutterSize={0.5}
                >
                  <KoobButton
                    style={{ marginLeft: 'auto' }}
                    variant="secondary"
                    type="button"
                    onClick={onSelectionFinished}
                  >
                    {t('misc:cancel')}
                  </KoobButton>

                  <KoobButton
                    type="submit"
                    disabled={!isValid}
                    variant="primary"
                    loading={isSubmitting}
                    testId="save-room"
                  >
                    {t('misc:save')}
                  </KoobButton>
                </Stack>
              </Form>
            )}
          </Formik>
        </>
      )}
    </>
  );
};

export default HotelDetailForm;
